import { useCallback, useState, type ReactNode } from 'react'
import { Modal, useWindowDimensions, View } from 'react-native'
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  type SharedValue
} from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { isWeb } from '@/constants/app'
import { WEB_VIEW_MAX_WIDTH } from '@/constants/config'

type SlideInModalProps = {
  visible: boolean
  offset: SharedValue<number>
  close: () => void
  children: ReactNode
}

function SlideInModal({ visible, offset, close, children }: SlideInModalProps) {
  const insets = useSafeAreaInsets()
  const style = useAnimatedStyle(() => ({ transform: [{ translateX: offset.value }] }))
  const { width } = useWindowDimensions()
  const maxWidth = isWeb ? WEB_VIEW_MAX_WIDTH : width
  const sideWidth = Math.max((width - maxWidth) / 2, 0)
  return (
    <Modal visible={visible} transparent={true} onRequestClose={close}>
      <Animated.View
        style={[
          { flex: 1, flexDirection: 'row', backgroundColor: 'white', paddingTop: insets.top },
          style
        ]}
      >
        <View className={'bg-white'} style={{ width: sideWidth }} />
        <View className={'flex-1'}>{children}</View>
        <View className={'bg-white'} style={{ width: sideWidth }} />
      </Animated.View>
      <View className={'absolute inset-y-0 left-0 bg-white'} style={{ width: sideWidth }} />
      <View className={'absolute inset-y-0 right-0 bg-white'} style={{ width: sideWidth }} />
    </Modal>
  )
}

export default SlideInModal

export function useSlideInModal(duration = 300) {
  const { width: deviceWidth } = useWindowDimensions()
  const [visible, setVisible] = useState(false)
  const offset = useSharedValue(deviceWidth)
  const open = useCallback(() => {
    setVisible(true)
    offset.value = withTiming(0, { duration })
  }, [duration, offset])

  const close = useCallback(() => {
    offset.value = withTiming(deviceWidth, { duration }, () => runOnJS(setVisible)(false))
  }, [duration, offset])

  return {
    visible,
    open,
    close,
    offset
  }
}
